import React, { forwardRef, useEffect, useState, useRef } from 'react'
import { TableVirtuoso } from 'react-virtuoso'
import { getColumns, getDateAndDateTime, getRowData } from '../util'
import {
  Box,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material'
import { ACTIVITY_TYPES, STANDARD_COLORS } from '../../../utility/constants'
import { fileServices } from '../../../services/fileServices'
import { useAuthContext } from '../../../hooks/useAuthContext'
import { GetApp as GetAppIcon, Visibility } from '@mui/icons-material'
import LongMenu from '../../../pages/DataEntryPage/Fields/LongMenu'
import StandaloneFilePreview from '../../Dashboard/PreviewComponent/components/StandaloneFilePreview'
import { FIELD_TYPES } from '../../../utility/constants'
import { extractDisplayName } from '../../../utility/fileUtil'

const downloadFile = async (user, value) => {
  await fileServices.downloadFile(user, value)
}

const getCellColor = (data, activityType) => {
  if (!data) return null

  if (activityType === ACTIVITY_TYPES.REVIEW) {
    if (data.review === undefined || data.review === null) return null
    return data.review === true
      ? STANDARD_COLORS.PREVIEW_ACCEPTED
      : STANDARD_COLORS.PREVIEW_REJECTED
  }

  if (activityType === ACTIVITY_TYPES.LONG_RUNNING) {
    if (data.review === undefined || data.review === null) return null
    return data.review === true
      ? STANDARD_COLORS.PREVIEW_ACCEPTED
      : STANDARD_COLORS.PREVIEW_REJECTED
  }

  return null
}

const renderValue = (data, { user, showPreview, color }) => {
  if (!data) return null

  switch (data.data_structure) {
    case FIELD_TYPES.FILE:
      return (
        <>
          <span style={{ color }}>{extractDisplayName(data.value)}</span>
          {data.value?.length > 0 && (
            <LongMenu
              options={[
                {
                  label: 'Preview',
                  icon: (
                    <IconButton size="small">
                      <Visibility />
                    </IconButton>
                  ),
                  onClick: () => showPreview(data.value),
                },
                {
                  label: 'Download',
                  icon: (
                    <IconButton size="small">
                      <GetAppIcon />
                    </IconButton>
                  ),
                  onClick: () => downloadFile(user, data.value),
                },
              ]}
            />
          )}
        </>
      )

    case FIELD_TYPES.DATE:
      return <span style={{ color }}>{getDateAndDateTime(data.value, data.data_structure)}</span>

    case FIELD_TYPES.DATETIME:
      return <span style={{ color }}>{getDateAndDateTime(data.value, data.data_structure)}</span>

    default:
      return <span style={{ color }}>{data.value}</span>
  }
}

function fixedHeaderContent(columns, headerHeight, headerRef) {
  return (
    <TableRow ref={headerRef}>
      {columns.map((column) => (
        <TableCell
          key={column.dataKey}
          variant="head"
          style={{
            width: column.width,
            position: column.isFrozen ? 'sticky' : 'relative',
            zIndex: column.isFrozen ? 300 : 200,
            top: 0,
            backgroundColor: 'white',
            left: column.isFrozen ? `${column.leftOffset}px` : 'auto',
            height: `${headerHeight}px`,
          }}
        >
          {column.label}
        </TableCell>
      ))}
    </TableRow>
  )
}

function rowContent(index, row, columns, activityType, user, showPreview, headerHeight) {
  if (!row.is_header) {
    return (
      <>
        {columns.map((column) => {
          let data = row[column.dataKey]

          if (column.dataKey === 'slNo') {
            data = { value: index + 1 }
          }

          const isFrozen = column.isFrozen
          const leftOffset = column.leftOffset

          const color = getCellColor(data, activityType)

          return (
            <TableCell
              key={column.dataKey}
              align={column.numeric ? 'right' : 'left'}
              style={{
                padding: 4,
                position: isFrozen ? 'sticky' : 'relative',
                left: isFrozen ? `${leftOffset}px` : 'auto',
                backgroundColor: 'white',
                width: column.width,
                zIndex: isFrozen ? 150 : 1,
                height: '100%',
                top: isFrozen ? `${headerHeight}px` : 'auto',
              }}
            >
              <div
                className="preview-timestramp"
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  overflow: 'wrap',
                  wordBreak: 'break-all',
                }}
              >
                {renderValue(data, { user, showPreview, color })}
              </div>
            </TableCell>
          )
        })}
      </>
    )
  } else {
    return (
      <TableCell
        style={{ fontWeight: 'bold', backgroundColor: '#f0f0f0', padding: 4 }}
        colSpan={columns.length}
      >
        <Box minHeight={'27px'} display={'flex'} alignItems={'center'}>
          <Typography variant="subtitle2" sx={{ fontSize: 12 }}>
            {row.value}
          </Typography>
        </Box>
      </TableCell>
    )
  }
}

const VirtuosoTableComponents = {
  Scroller: forwardRef((props, ref) => (
    <TableContainer className="responsiveTable" component={Paper} {...props} ref={ref} />
  )),
  Table: (props) => <Table {...props} sx={{ borderCollapse: 'separate', tableLayout: 'fixed' }} />,
  TableHead: forwardRef((props, ref) => <TableHead {...props} ref={ref} />),
  TableRow,
  TableBody: forwardRef((props, ref) => <TableBody {...props} ref={ref} />),
}

const VirtualizedTable = ({
  activityType,
  rows,
  tableSequenceAndHeaders = [],
  subfields,
  values,
  reviews,
  hiddenColumns,
  listData,
  dynamicListData,
  previewCurrency,
  freezeColumnId,
}) => {
  const { user } = useAuthContext()
  const [columns, setColumns] = useState([])
  const [filePreview, setFilePreview] = useState(null)

  const headerRef = useRef(null)
  const [headerHeight, setHeaderHeight] = useState(0)

  useEffect(() => {
    if (headerRef.current) {
      const measuredHeight = headerRef.current.getBoundingClientRect().height
      setHeaderHeight(measuredHeight)
    }
  }, [columns])

  useEffect(() => {
    const updatedColumns = getColumns(subfields, hiddenColumns, freezeColumnId)
    setColumns(updatedColumns)
  }, [subfields, hiddenColumns, freezeColumnId])

  return (
    <>
      <Paper style={{ height: 240, width: '100%' }}>
        <TableVirtuoso
          data={getRowData(
            rows,
            tableSequenceAndHeaders,
            values,
            reviews,
            subfields,
            hiddenColumns,
            listData,
            dynamicListData,
            previewCurrency,
            freezeColumnId,
          )}
          components={VirtuosoTableComponents}
          fixedHeaderContent={() => fixedHeaderContent(columns, headerHeight, headerRef)}
          itemContent={(_index, row) =>
            rowContent(
              _index,
              row,
              columns,
              activityType,
              user,
              (file) => setFilePreview(file),
              headerHeight,
            )
          }
        />
      </Paper>

      <StandaloneFilePreview
        fileKey={filePreview}
        open={filePreview != null}
        onClose={() => setFilePreview(null)}
      />
    </>
  )
}

export default VirtualizedTable
